import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const cfg = await api.get("/auth/config").then((r) => r.data);
      if (cfg.user_count === 0) {
        // Fresh install — allow through without login (owner claims farm on first sign-in)
        setBootstrapping(true);
        setUser(null);
        setLoading(false);
        return;
      }
      setBootstrapping(false);
      const me = await api.get("/auth/me").then((r) => r.data);
      setUser(me);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = { user, setUser, loading, bootstrapping, refresh: checkAuth, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

/** Helper: does this user have permission for a UI capability? */
export function can(user, cap) {
  if (!user) {
    // Bootstrap mode — allow every capability EXCEPT team management
    // (which requires an authenticated owner to actually work).
    return cap !== "manage.team";
  }
  const { role, scopes = [] } = user;

  // Financial features — Owner + Read-only (view). Read-only can't edit.
  const financialView = new Set([
    "view.reports", "view.balance_sheet", "view.partner_invoices", "view.customer_pnl",
    "view.internal_savings", "view.customer_outstanding", "view.projections", "view.invoices",
    "view.loans", "view.assets", "view.crop_prices", "view.mileage_reports",
    "view.fleet_pnl", "view.markets",
  ]);
  const financialWrite = new Set([
    "edit.reports", "edit.balance_sheet", "edit.partner_invoices", "edit.customer_pnl",
    "edit.internal_savings", "edit.projections", "edit.invoices",
    "edit.loans", "edit.assets", "edit.crop_prices",
  ]);

  if (financialView.has(cap)) return role === "owner" || role === "readonly";
  if (financialWrite.has(cap)) return role === "owner";

  // Team management
  if (cap === "manage.team") return role === "owner";
  if (cap === "manage.notifications") return role === "owner" || role === "manager";

  // Read-only can view anything else but not write
  if (cap.startsWith("edit.") && role === "readonly") return false;

  // Hand scope enforcement
  const scopeMap = {
    livestock: new Set([
      "view.livestock", "edit.livestock", "view.medications", "edit.medications",
      "view.care_events", "edit.care_events",
    ]),
    equipment: new Set([
      "view.equipment", "edit.equipment", "view.repairs", "edit.repairs",
      "view.maintenance", "edit.maintenance",
    ]),
    yields_fields: new Set([
      "view.fields", "edit.fields", "view.yields", "edit.yields",
      "view.inventory", "edit.inventory", "view.soil_samples", "edit.soil_samples",
      "view.field_applications", "edit.field_applications", "view.field_pnl",
    ]),
  };

  if (role === "hand") {
    for (const s of scopes) {
      if (scopeMap[s]?.has(cap)) return true;
    }
    // Allow generic UX bits Hand can always see
    const alwaysHand = new Set(["view.calendar", "view.dashboard", "view.reminders"]);
    return alwaysHand.has(cap);
  }

  // Owner + Manager can view/edit non-financial by default
  return true;
}
